import { Edit } from '@mui/icons-material';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  createDocumentsFromCompany,
  getAvailableSubProductsForFactoring,
  getDocumentsAvailablesForFactoring,
} from 'lib/api';
import CompanyBusinessSearcher from 'lib/common/CompanyBusinessSearcher';
import GlobalSearcher, { Filter } from 'lib/common/GlobalSearcher';
import RutBusinessSearcher from 'lib/common/RutBusinessSearcher';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions from 'lib/designSystem/HeaderWithActions';
import SimulationBanner from 'lib/designSystem/SimulationBanner';
import TableWithPagination from 'lib/designSystem/TableWithPagination';
import { formatDate } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import SimulatorDialog from '../../Factoring/components/Simulator';
import ClientInfo from './components/ClientInfo';
import CreditNoteAlert from './components/CreditNoteAlert';
import DocumentImporter, {
  type DocumentImporterConfig,
  getImporterConfigForCategory,
  type ImporterDocumentRow,
} from './components/documentImporter';
import EditDocumentManually from './components/editDocument';
import SubProductSelector from './components/SubProductSelector';
import SuccessSnackbar from './components/SuccessSnackbar';
import {
  type ActiveTab,
  type Business,
  type CreatedDocument,
  type DocumentConfiguration,
  type DocumentsCreatedPayload,
  type DocumentsSelectorDialogProps,
  type DocumentWithMetadata,
  type EnrolledType,
  type PaginationState,
  type PrioritizedDocState,
  type PrioritizedSource,
  type RepeatedDocument,
  type SearchDocumentsParams,
  type SubProduct,
} from './types';
import {
  buildDocumentKey,
  getDocumentKeyType,
  mergePriorityKeys,
  normalizePriorityKeys,
  parseDocumentKey,
} from './utils/documentKey';
import { buildDocumentsQuery } from './utils/queryBuilder';

const DocumentsSelectorDialog = (props: DocumentsSelectorDialogProps) => {
  const {
    open,
    setOpen,
    setSelectedDocuments,
    selectedDocuments,
    setNewOpenShoppingCart,
    business,
    setBusiness,
    subProductSelected,
    setSubProductSelected,
    setSubProduct,
    backwards,
    setBackwards,
    setSuccessOpen,
  } = props;
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const [selected, setSelected] = useState<string[]>([]);
  const [searchedBySubProduct, setSearchedBySubProduct] = useState(false);
  const [documents, setDocuments] = useState<DocumentWithMetadata[]>([]);
  const [subProducts, setSubProducts] = useState<SubProduct[]>([]);
  const [isEnrolled, setIsEnrolled] = useState<boolean | undefined>(undefined);
  const [createWithoutCreditline, setCreateWithoutCreditline] = useState(false);
  const [openDocumentImporter, setOpenDocumentImporter] = useState(false);
  const [documentImporterConfig, setDocumentImporterConfig] =
    useState<DocumentImporterConfig | null>(null);
  const [openEditDocument, setOpenEditDocument] = useState<boolean>(false);
  const [documentToEdit, setDocumentToEdit] =
    useState<DocumentWithMetadata | null>(null);
  const [configuration, setConfiguration] =
    useState<DocumentConfiguration | null>(null);
  const [cachedDocuments, setCachedDocuments] = useState<
    DocumentWithMetadata[]
  >([]);
  const [isSimulation, setIsSimulation] = useState(false);
  const [openSimulator, setOpenSimulator] = useState(false);
  const [activeTab, setActiveTab] = useState<ActiveTab>('company');
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const [prioritizedDocuments, setPrioritizedDocuments] = useState<
    Record<string, PrioritizedDocState>
  >({});
  const [documentSources, setDocumentSources] = useState<
    Record<string, PrioritizedSource>
  >({});
  const [persistedManualKeys, setPersistedManualKeys] = useState<string[]>([]);
  const [persistedInvoiceKeys, setPersistedInvoiceKeys] = useState<string[]>(
    [],
  );

  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [stakeholderIdentifier, setStakeholderIdentifier] = useState('');
  const [stakeholderName, setStakeholderName] = useState('');
  const [enrolledType, setEnrolledType] = useState<EnrolledType>(null);
  const [folio, setFolio] = useState('');
  const PAGE_SIZE = 10;
  const [pagination, setPagination] = useState<PaginationState>({
    page: 1,
    pageSize: PAGE_SIZE,
    total: 0,
    totalPages: 0,
  });

  const keyType = getDocumentKeyType(subProductSelected);

  const deriveEnrolledType = ({
    forceType,
    responseIsEnrolled,
    currentType,
  }: {
    forceType?: string;
    responseIsEnrolled?: boolean;
    currentType?: EnrolledType;
  }): EnrolledType => {
    if (forceType === 'manual' || forceType === 'sii') {
      return forceType;
    }

    if (responseIsEnrolled === false) {
      return 'manual';
    }

    if (responseIsEnrolled === true) {
      if (currentType === 'manual' || currentType === 'sii') {
        return currentType;
      }
      return 'sii';
    }

    if (currentType === 'manual' || currentType === 'sii') {
      return currentType;
    }

    return null;
  };

  const mergePrioritizedEntries = (
    base: Record<string, PrioritizedDocState>,
    entries: PrioritizedDocState[],
  ): Record<string, PrioritizedDocState> => {
    const next: Record<string, PrioritizedDocState> = { ...base };

    entries.forEach((entry) => {
      if (!entry.folio || !entry.businessIdentifier) {
        return;
      }

      const existing = next[entry.key];
      if (existing) {
        const statusesSet = new Set<'created' | 'repeated' | 'selected'>(
          existing.statuses,
        );
        entry.statuses.forEach((status) => statusesSet.add(status));
        next[entry.key] = {
          ...existing,
          source: existing.source || entry.source,
          statuses: Array.from(statusesSet),
        };
      } else {
        next[entry.key] = {
          key: entry.key,
          folio: entry.folio,
          businessIdentifier: entry.businessIdentifier,
          source: entry.source,
          statuses: Array.from(new Set(entry.statuses)),
        };
      }
    });

    return next;
  };

  const extractKeysBySource = (
    state: Record<string, PrioritizedDocState>,
    source: PrioritizedSource,
  ) =>
    Object.values(state)
      .filter((doc) => doc.source === source)
      .map((doc) => doc.key);

  const addManualPriorityKeys = (keys: string[]) => {
    const normalized = normalizePriorityKeys(keys);
    if (!normalized.length) return;
    setPersistedManualKeys((prev) => mergePriorityKeys(prev, normalized));
  };

  const addInvoicePriorityKeys = (keys: string[]) => {
    const normalized = normalizePriorityKeys(keys);
    if (!normalized.length) return;
    setPersistedInvoiceKeys((prev) => mergePriorityKeys(prev, normalized));
  };

  const clearPriorityKeys = () => {
    setPersistedManualKeys([]);
    setPersistedInvoiceKeys([]);
  };

  const generateQuery = ({
    page,
    pageSize,
    overrideEnrolledType,
    manualOverrideKeys,
    invoiceOverrideKeys,
  }: {
    page: number;
    pageSize: number;
    overrideEnrolledType?: string;
    manualOverrideKeys?: string[];
    invoiceOverrideKeys?: string[];
  }) => {
    return buildDocumentsQuery({
      page,
      pageSize,
      folio,
      stakeholderIdentifier,
      stakeholderName,
      startDate,
      endDate,
      enrolledType,
      selectedIds: selected,
      overrideEnrolledType,
      manualOverrideKeys: manualOverrideKeys ?? persistedManualKeys,
      invoiceOverrideKeys: invoiceOverrideKeys ?? persistedInvoiceKeys,
      isEnrolled,
    });
  };

  const filters: Filter[] = [
    {
      label: 'Fecha de emisión',
      type: 'date',
      value: startDate,
      setter: setStartDate,
      dateKey: 'startDate',
    },
    {
      label: 'Fecha de confirmación',
      type: 'date',
      value: endDate,
      setter: setEndDate,
      dateKey: 'endDate',
    },

    {
      label: 'Nombre deudor',
      type: 'autocomplete',
      value: stakeholderName,
      key: 'stakeholderName',
      setter: setStakeholderName,
    },
    {
      label: 'Rut contraparte',
      type: 'text',
      value: stakeholderIdentifier,
      setter: setStakeholderIdentifier,
    },
    {
      label: 'Folio',
      type: 'text',
      value: folio,
      setter: setFolio,
    },
  ];

  const handleSearchSuggestedDocuments = async ({
    page,
    pageSize,
    forceEnrolledType,
    manualOverrideKeys,
    invoiceOverrideKeys,
    subProductOverride,
  }: SearchDocumentsParams) => {
    try {
      const manualKeysForQuery = manualOverrideKeys ?? persistedManualKeys;
      const invoiceKeysForQuery = invoiceOverrideKeys ?? persistedInvoiceKeys;

      const query = generateQuery({
        page,
        pageSize,
        overrideEnrolledType: forceEnrolledType,
        manualOverrideKeys: manualKeysForQuery,
        invoiceOverrideKeys: invoiceKeysForQuery,
      });

      const subProductToUse = subProductOverride ?? subProductSelected;
      const resolvedSubProductId =
        typeof subProductToUse === 'string' ? subProductToUse : '';

      if (!resolvedSubProductId) {
        return undefined;
      }

      if (!business?.id) {
        return undefined;
      }

      const response = await getDocumentsAvailablesForFactoring(
        dispatch,
        business.id,
        resolvedSubProductId,
        query,
      );
      const configuration = response?.configuration;
      const documents = response?.data || [];
      const pagination = response?.pagination;
      const responseIsEnrolled = response?.isEnrolled;

      const finalEnrolledType = deriveEnrolledType({
        forceType: forceEnrolledType,
        responseIsEnrolled,
        currentType: enrolledType,
      });

      setIsEnrolled(responseIsEnrolled);
      setEnrolledType(finalEnrolledType);

      if (configuration) {
        setConfiguration(configuration);
      }
      if (pagination) {
        setPagination(pagination);
      }

      const documentsWithCache = documents.map((doc: DocumentWithMetadata) => {
        const cachedDoc = cachedDocuments.find(
          (cached: DocumentWithMetadata) => cached.id === doc.id,
        );
        if (cachedDoc) {
          return {
            ...doc,
            dueDate: cachedDoc.dueDate || doc.dueDate,
            tempDueDate: cachedDoc.tempDueDate || doc.tempDueDate,
            paymentDate: cachedDoc.paymentDate || doc.paymentDate,
          };
        }
        return doc;
      });

      setDocuments(documentsWithCache);
      setSearchedBySubProduct(true);

      const effectiveTypeKey =
        finalEnrolledType ?? (responseIsEnrolled ? 'sii' : 'manual');
      const typeUsed: PrioritizedSource =
        effectiveTypeKey === 'manual' ? 'manual' : 'invoice';

      if (documents?.length || response?.preSelectedDocuments?.length) {
        const sourcesToRegister = [
          ...(documents ?? []),
          ...(response?.preSelectedDocuments ?? []),
        ];
        if (sourcesToRegister.length) {
          setDocumentSources((prev) => {
            const next = { ...prev };
            sourcesToRegister.forEach((doc: DocumentWithMetadata) => {
              if (doc?.id) {
                next[doc.id] = typeUsed;
              }
            });
            return next;
          });
        }
      }

      return response;
    } catch (error) {
      console.error(error);
    }
  };

  const triggerSearch = async (subProductOverride?: string) => {
    setLoading(true);
    const response = await handleSearchSuggestedDocuments({
      page: 1,
      pageSize: PAGE_SIZE,
      manualOverrideKeys: persistedManualKeys,
      invoiceOverrideKeys: persistedInvoiceKeys,
      subProductOverride,
    });
    setLoading(false);
    return response;
  };

  const handleDocumentsCreated = async (
    payload: DocumentsCreatedPayload,
    count: number,
  ) => {
    const createdDocuments = payload?.created || [];
    const repeatedDocs = payload?.repeated || [];
    const docKeyType = getDocumentKeyType(subProductSelected);

    if (isEnrolled) {
      setEnrolledType('manual');
    }

    const createdEntries: PrioritizedDocState[] = createdDocuments.map(
      (doc: CreatedDocument) => {
        const key = buildDocumentKey(docKeyType, {
          folio: doc.folio,
          businessIdentifier: doc.businessIdentifier,
          stakeholderIdentifier:
            docKeyType === 'check' ? doc.stakeholderIdentifier : undefined,
        });
        return {
          key,
          folio: doc.folio,
          businessIdentifier: doc.businessIdentifier,
          source: 'manual' as PrioritizedSource,
          statuses: ['created'] as Array<'created' | 'repeated' | 'selected'>,
        };
      },
    );

    const repeatedEntries = repeatedDocs
      .map((item: string | RepeatedDocument) => {
        if (typeof item === 'object' && item !== null) {
          const folio = item.folio;
          const businessIdentifier = item.businessIdentifier;
          if (!folio || !businessIdentifier) {
            return null;
          }
          const docKey = buildDocumentKey(docKeyType, {
            folio,
            businessIdentifier,
            stakeholderIdentifier:
              docKeyType === 'check' && 'stakeholderIdentifier' in item
                ? (item as CreatedDocument).stakeholderIdentifier
                : undefined,
          });
          return {
            key: docKey,
            folio,
            businessIdentifier,
            source: 'manual' as PrioritizedSource,
            statuses: ['repeated'] as Array<
              'created' | 'repeated' | 'selected'
            >,
          };
        }
        if (typeof item === 'string') {
          const parsed = parseDocumentKey(item);
          if (!parsed?.folio || !parsed?.businessIdentifier) {
            return null;
          }
          const docKey = buildDocumentKey(docKeyType, parsed);
          return {
            key: docKey,
            folio: parsed.folio,
            businessIdentifier: parsed.businessIdentifier,
            source: 'manual' as PrioritizedSource,
            statuses: ['repeated'] as Array<
              'created' | 'repeated' | 'selected'
            >,
          };
        }
        return null;
      })
      .filter(Boolean) as PrioritizedDocState[];

    const mergedState = mergePrioritizedEntries(prioritizedDocuments, [
      ...createdEntries,
      ...repeatedEntries,
    ]);

    const documentsMap = new Map<string, DocumentWithMetadata>();

    createdDocuments.forEach((doc: CreatedDocument) => {
      if (doc?.folio && doc?.businessIdentifier) {
        const key = buildDocumentKey(docKeyType, {
          folio: doc.folio,
          businessIdentifier: doc.businessIdentifier,
          stakeholderIdentifier:
            docKeyType === 'check' ? doc.stakeholderIdentifier : undefined,
        });
        const documentToMap = {
          ...doc,
          amount: doc.amount ?? 0,
          issuedDate: doc.issuedDate ?? '',
          __isCreated: true,
        } as DocumentWithMetadata;
        documentsMap.set(key, documentToMap);
      }
    });

    repeatedDocs.forEach(
      (item: string | CreatedDocument | RepeatedDocument) => {
        if (
          typeof item === 'object' &&
          item?.folio &&
          item?.businessIdentifier
        ) {
          const key = buildDocumentKey(docKeyType, {
            folio: item.folio,
            businessIdentifier: item.businessIdentifier,
            stakeholderIdentifier:
              docKeyType === 'check' && 'stakeholderIdentifier' in item
                ? (item as CreatedDocument).stakeholderIdentifier
                : undefined,
          });
          documentsMap.set(key, {
            ...item,
            amount:
              'amount' in item && typeof item.amount === 'number'
                ? item.amount
                : 0,
            issuedDate:
              'issuedDate' in item && typeof item.issuedDate === 'string'
                ? item.issuedDate
                : '',
            __isRepeated: true,
          } as DocumentWithMetadata);
        }
      },
    );

    const manualKeysOverride = extractKeysBySource(mergedState, 'manual');
    const invoiceKeysOverride = extractKeysBySource(
      prioritizedDocuments,
      'invoice',
    );

    if (manualKeysOverride.length) {
      addManualPriorityKeys(manualKeysOverride);
    }

    const response = await handleSearchSuggestedDocuments({
      page: 1,
      pageSize: PAGE_SIZE,
      forceEnrolledType: isEnrolled ? 'manual' : undefined,
      manualOverrideKeys: manualKeysOverride,
      invoiceOverrideKeys: invoiceKeysOverride,
      subProductOverride: subProductSelected,
    });

    const foundDocuments: DocumentWithMetadata[] = [];
    const foundIds: string[] = [];

    const allDocuments = [
      ...(response?.data || []),
      ...(response?.preSelectedDocuments || []),
    ];

    if (allDocuments.length > 0) {
      for (const docKey of manualKeysOverride) {
        const parsed = parseDocumentKey(docKey);
        if (!parsed?.folio || !parsed?.businessIdentifier) {
          continue;
        }
        const { folio, businessIdentifier, stakeholderIdentifier } = parsed;
        const document = allDocuments.find(
          (item: DocumentWithMetadata) =>
            item.folio === folio &&
            item.businessIdentifier === businessIdentifier &&
            (stakeholderIdentifier == null ||
              item.stakeholderIdentifier === stakeholderIdentifier),
        );
        if (document) {
          const mapData = documentsMap.get(docKey);
          const mergedDocument: DocumentWithMetadata = mapData
            ? {
                ...document,
                dueDate: mapData.dueDate || document.dueDate,
                id:
                  mapData.__isRepeated && mapData.id
                    ? mapData.id
                    : document.id || mapData.id,
              }
            : document;

          foundDocuments.push(mergedDocument);
          if (mergedDocument.id) {
            foundIds.push(mergedDocument.id);
          }
        }
      }
    }

    const newSelected = [...new Set([...selected, ...foundIds])];
    setSelected(newSelected);

    if (foundDocuments.length) {
      setDocumentSources((prev) => {
        const next = { ...prev };
        foundDocuments.forEach((doc: DocumentWithMetadata) => {
          if (doc?.id) {
            next[doc.id] = 'manual';
          }
        });
        return next;
      });
    }

    const newCachedDocuments = [...cachedDocuments];
    for (const document of foundDocuments) {
      const cachedIds = newCachedDocuments.map(
        (doc: DocumentWithMetadata) => doc.id,
      );
      if (document?.id && !cachedIds.includes(document.id)) {
        newCachedDocuments.push(document);
      }
    }
    setCachedDocuments(newCachedDocuments);

    if (foundDocuments.length > 0) {
      setDocuments((prevDocuments) => {
        const foundDocumentsMap = new Map<string, DocumentWithMetadata>();
        foundDocuments.forEach((doc: DocumentWithMetadata) => {
          if (doc.id) {
            foundDocumentsMap.set(doc.id, doc);
          }
        });

        const updatedDocuments = prevDocuments.map(
          (doc: DocumentWithMetadata) => {
            const foundDoc = doc.id ? foundDocumentsMap.get(doc.id) : undefined;
            if (foundDoc) {
              return foundDoc;
            }
            return doc;
          },
        );

        const existingIds = new Set(
          prevDocuments.map((d: DocumentWithMetadata) => d.id).filter(Boolean),
        );
        const documentsToAdd = foundDocuments.filter(
          (fd: DocumentWithMetadata) => fd.id && !existingIds.has(fd.id),
        );

        return [...updatedDocuments, ...documentsToAdd];
      });
    }

    const selectionEntries: PrioritizedDocState[] = foundDocuments
      .map((doc: DocumentWithMetadata) => {
        if (!doc?.folio || !doc?.businessIdentifier) {
          return null;
        }
        const key = buildDocumentKey(keyType, {
          folio: doc.folio,
          businessIdentifier: doc.businessIdentifier,
          stakeholderIdentifier:
            keyType === 'check' ? doc.stakeholderIdentifier : undefined,
        });
        return {
          key,
          folio: doc.folio,
          businessIdentifier: doc.businessIdentifier,
          source: 'manual' as PrioritizedSource,
          statuses: ['selected'] as Array<'created' | 'repeated' | 'selected'>,
        };
      })
      .filter(Boolean) as PrioritizedDocState[];

    const finalState = mergePrioritizedEntries(mergedState, selectionEntries);
    setPrioritizedDocuments(finalState);

    const foundCount = foundIds.length;
    if (foundCount > 0) {
      setSuccessMessage(
        `${foundCount} documento${foundCount > 1 ? 's' : ''} agregado${foundCount > 1 ? 's' : ''} y seleccionado${foundCount > 1 ? 's' : ''} automáticamente`,
      );
      setShowSuccessBanner(true);
    } else {
      setSuccessMessage(
        `Se agregaron ${count} documento${count > 1 ? 's' : ''} pero no se pudieron seleccionar automáticamente. Los puedes buscar en la tabla.`,
      );
      setShowSuccessBanner(true);
    }
  };

  const setAvailableSubProducts = async () => {
    setLoading(true);
    try {
      if (!business?.id) {
        return;
      }
      const { subProducts, canCreateWithoutCreditLine } =
        await getAvailableSubProductsForFactoring(dispatch, business.id);
      setCreateWithoutCreditline(canCreateWithoutCreditLine);
      setSubProducts(subProducts);
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  const handleSelectProduct = (id: string) => {
    if (subProductSelected && subProductSelected !== id) {
      clearPriorityKeys();
      setSelected([]);
      setSelectedDocuments([]);
      setCachedDocuments([]);
      setDocumentSources({});
      setPrioritizedDocuments({});
    }

    if (!id) {
      setSubProductSelected('');
      setSubProduct(undefined);
      setSearchedBySubProduct(false);
      return;
    }

    const subProduct = subProducts.find(
      (subProduct: SubProduct) => subProduct.id === id,
    );
    setSubProductSelected(id);
    setSubProduct(subProduct);
    setIsEnrolled(undefined);
    setEnrolledType(null);
    setDocuments([]);

    if (searchedBySubProduct) {
      setSearchedBySubProduct(false);
    }
  };

  const handleBusinessSelection = (businessSelected: Business | null) => {
    setBusiness(businessSelected);

    const activeValue =
      businessSelected && typeof businessSelected.active === 'boolean'
        ? businessSelected.active
        : undefined;

    setIsEnrolled(activeValue);
    if (activeValue !== undefined) {
      setEnrolledType(activeValue ? 'sii' : 'manual');
    } else {
      setEnrolledType(null);
    }

    setSelected([]);
    setSelectedDocuments([]);
    setCachedDocuments([]);
    setPrioritizedDocuments({});
    setDocumentSources({});
    clearPriorityKeys();
    setSubProductSelected('');
    setSubProduct(undefined);
    setSearchedBySubProduct(false);
  };

  const handleOpenDialogAdder = () => {
    const subProduct = subProducts.find(
      (subProduct: SubProduct) => subProduct.id === subProductSelected,
    );
    setDocumentImporterConfig(
      getImporterConfigForCategory(subProduct?.category),
    );
    setOpenDocumentImporter(true);
  };

  const handleDocumentImporterImport = async (
    documentsToImport: ImporterDocumentRow[],
  ) => {
    if (!business) return;
    const formatDocumentsWithDate = documentsToImport.map((doc) => ({
      ...doc,
      issuedDate: new Date(doc.issuedDate).toISOString(),
      receiptDate: new Date(doc.issuedDate).toISOString(),
      amount: doc.amount,
    }));
    const response = await createDocumentsFromCompany(dispatch, {
      documents: formatDocumentsWithDate,
      businessIdentifier: business.identifier,
      subProductId: subProductSelected,
    });
    const createdDocuments = response?.documentCreated || [];
    const repeatedDocuments = response?.documentsRepetead || [];
    setSuccessOpen(
      documentsToImport.length === 1
        ? 'Documento agregado correctamente'
        : 'Documentos agregados correctamente',
    );
    setOpenDocumentImporter(false);
    setDocumentImporterConfig(null);
    if (createdDocuments.length > 0 || (repeatedDocuments?.length ?? 0) > 0) {
      await handleDocumentsCreated(
        { created: createdDocuments, repeated: repeatedDocuments ?? [] },
        documentsToImport.length,
      );
    } else {
      await handleSearchSuggestedDocuments({
        page: 1,
        pageSize: 10,
        subProductOverride: subProductSelected,
      });
    }
  };

  const handleSetSelected = (selectedIds: string[] | string) => {
    const normalizedSelectedIds = Array.isArray(selectedIds)
      ? selectedIds
      : [selectedIds];

    const docsById = new Map<string, DocumentWithMetadata>();
    [...documents, ...cachedDocuments, ...(selectedDocuments || [])].forEach(
      (doc: DocumentWithMetadata) => {
        if (doc?.id) {
          docsById.set(doc.id, doc);
        }
      },
    );

    const resolveDoc = (id: string): DocumentWithMetadata | undefined =>
      docsById.get(id) ||
      documents.find((item: DocumentWithMetadata) => item.id === id) ||
      cachedDocuments.find((item: DocumentWithMetadata) => item.id === id) ||
      (selectedDocuments || []).find(
        (item: DocumentWithMetadata) => item.id === id,
      );

    const selectedDocs = normalizedSelectedIds
      .map((id: string): DocumentWithMetadata | null => {
        const document = resolveDoc(id);
        if (!document?.folio || !document?.businessIdentifier) {
          return null;
        }

        const key = buildDocumentKey(keyType, {
          folio: document.folio,
          businessIdentifier: document.businessIdentifier,
          stakeholderIdentifier:
            keyType === 'check' ? document.stakeholderIdentifier : undefined,
        });
        const source =
          documentSources[id] ??
          document.__source ??
          (enrolledType === 'manual' ? 'manual' : 'invoice');

        return {
          ...document,
          __source: source,
          __id: document?.id ?? key,
          __key: key,
        };
      })
      .filter((doc): doc is DocumentWithMetadata => doc !== null);

    setSelected(normalizedSelectedIds);
    setSelectedDocuments(selectedDocs);

    if (selectedDocs.length) {
      const nextCache = [...cachedDocuments];
      const cacheIds = new Set(
        nextCache.map((doc: DocumentWithMetadata) => doc.id),
      );
      selectedDocs.forEach((doc: DocumentWithMetadata) => {
        if (doc?.id && !cacheIds.has(doc.id)) {
          nextCache.push(doc);
          cacheIds.add(doc.id);
        }
      });
      setCachedDocuments(nextCache);
    }

    const manualKeys = normalizePriorityKeys(
      selectedDocs
        .filter((doc: DocumentWithMetadata) => doc.__source === 'manual')
        .map((doc: DocumentWithMetadata) => doc.__key || ''),
    );
    const invoiceKeys = normalizePriorityKeys(
      selectedDocs
        .filter((doc: DocumentWithMetadata) => doc.__source === 'invoice')
        .map((doc: DocumentWithMetadata) => doc.__key || ''),
    );

    setPersistedManualKeys(manualKeys);
    setPersistedInvoiceKeys(invoiceKeys);

    if (normalizedSelectedIds.length) {
      setDocumentSources((prev) => {
        const next = { ...prev };
        normalizedSelectedIds.forEach((id: string) => {
          const record = resolveDoc(id);
          if (record) {
            const key =
              record?.id ??
              buildDocumentKey(keyType, {
                folio: record.folio,
                businessIdentifier: record.businessIdentifier,
                stakeholderIdentifier:
                  keyType === 'check'
                    ? record.stakeholderIdentifier
                    : undefined,
              });
            next[id] =
              record.__source ??
              prev[id] ??
              (manualKeys.includes(key) ? 'manual' : 'invoice');
          }
        });
        return next;
      });
    } else {
      setDocumentSources({});
    }
  };

  const cantSelectDocuments = (document: DocumentWithMetadata) => {
    if (!configuration) {
      return false;
    }
    if (configuration.showOnlyAvailablesNotInRequest) {
      if (document.requestId) {
        return true;
      }
    }
    if (configuration.dontFilterOwnInvoices) {
      if (document.cessionaryIdentifier === configuration.companyIdentifier) {
        return false;
      }
    }
    if (configuration.showOnlyAvailables) {
      if (
        document.status === 'Transable' ||
        enrolledType === 'manual' ||
        !isEnrolled
      ) {
        return false;
      }
    }
    if (
      !configuration.dontFilterOwnInvoices &&
      !configuration.showOnlyAvailables
    ) {
      return false;
    }
    return true;
  };

  const handleSelectDocuments = async () => {
    setLoading(true);
    const documentselected: DocumentWithMetadata[] = cachedDocuments.filter(
      (document: DocumentWithMetadata) =>
        document.id && selected.includes(document.id),
    );
    if (!documentselected.length) {
      setLoading(false);
      return;
    }
    setLoading(false);
    const documentsWithMeta = documentselected.map(
      (document: DocumentWithMetadata) => {
        const source =
          (document.id ? documentSources[document.id] : undefined) ??
          (enrolledType === 'manual' ? 'manual' : 'invoice');

        const formattedDocument: DocumentWithMetadata = {
          ...document,
          __source: source,
        };

        if (document.dueDate) {
          formattedDocument.paymentDate = document.dueDate;
          formattedDocument.dueDate = document.dueDate;
          try {
            const dateObj = new Date(document.dueDate);
            if (!isNaN(dateObj.getTime())) {
              formattedDocument.tempDueDate = formatDate(dateObj, 'yyyy-mm-dd');
            }
          } catch (error) {
            console.error(error);
          }
        }

        return formattedDocument;
      },
    );

    const manualKeys = documentsWithMeta
      .filter(
        (document: DocumentWithMetadata) => document.__source === 'manual',
      )
      .map((document: DocumentWithMetadata) =>
        buildDocumentKey(keyType, {
          folio: document.folio,
          businessIdentifier: document.businessIdentifier,
          stakeholderIdentifier:
            keyType === 'check' ? document.stakeholderIdentifier : undefined,
        }),
      );
    const invoiceKeys = documentsWithMeta
      .filter(
        (document: DocumentWithMetadata) => document.__source === 'invoice',
      )
      .map((document: DocumentWithMetadata) =>
        buildDocumentKey('invoice', {
          folio: document.folio,
          businessIdentifier: document.businessIdentifier,
        }),
      );

    addManualPriorityKeys(manualKeys);
    addInvoicePriorityKeys(invoiceKeys);

    setSelectedDocuments(documentsWithMeta);
    addManualPriorityKeys(
      documentsWithMeta.map((doc: DocumentWithMetadata) =>
        buildDocumentKey(keyType, {
          folio: doc.folio,
          businessIdentifier: doc.businessIdentifier,
          stakeholderIdentifier:
            keyType === 'check' ? doc.stakeholderIdentifier : undefined,
        }),
      ),
    );
    setNewOpenShoppingCart(true);

    setPrioritizedDocuments({});
    setDocumentSources({});

    setOpen(false);
  };

  const handleSimulationSelect = () => {
    setOpenSimulator(true);
  };

  const handleBackwardsPrefill = async () => {
    setSearched(true);
    setSearchedBySubProduct(true);
    setLoading(true);

    const manualKeysToUse =
      persistedManualKeys.length > 0
        ? persistedManualKeys
        : selectedDocuments
            .filter(
              (doc: DocumentWithMetadata) =>
                (doc.__source ??
                  (enrolledType === 'manual' ? 'manual' : 'invoice')) ===
                'manual',
            )
            .map((doc: DocumentWithMetadata) =>
              buildDocumentKey(keyType, {
                folio: doc.folio,
                businessIdentifier: doc.businessIdentifier,
                stakeholderIdentifier:
                  keyType === 'check' ? doc.stakeholderIdentifier : undefined,
              }),
            );

    const invoiceKeysToUse =
      persistedInvoiceKeys.length > 0
        ? persistedInvoiceKeys
        : selectedDocuments
            .filter(
              (doc: DocumentWithMetadata) =>
                (doc.__source ??
                  (enrolledType === 'manual' ? 'manual' : 'invoice')) ===
                'invoice',
            )
            .map((doc: DocumentWithMetadata) =>
              buildDocumentKey('invoice', {
                folio: doc.folio,
                businessIdentifier: doc.businessIdentifier,
              }),
            );

    let targetSource: PrioritizedSource = 'manual';
    if (isEnrolled === true && invoiceKeysToUse.length > 0) {
      targetSource = 'invoice';
    } else if (manualKeysToUse.length > 0) {
      targetSource = 'manual';
    } else if (invoiceKeysToUse.length > 0) {
      targetSource = 'invoice';
    }

    const targetEnrolledType = targetSource === 'invoice' ? 'sii' : 'manual';
    setEnrolledType(targetEnrolledType);

    const response = await handleSearchSuggestedDocuments({
      page: 1,
      pageSize: pagination.pageSize,
      forceEnrolledType: targetEnrolledType,
      manualOverrideKeys: targetSource === 'manual' ? manualKeysToUse : [],
      invoiceOverrideKeys: targetSource === 'invoice' ? invoiceKeysToUse : [],
      subProductOverride: subProductSelected,
    });

    setLoading(false);

    if (!response) {
      return;
    }

    await setAvailableSubProducts();

    const allDocuments = [
      ...(response?.data || []),
      ...(response?.preSelectedDocuments || []),
    ];

    const keysToUse =
      targetSource === 'invoice' ? invoiceKeysToUse : manualKeysToUse;

    const foundDocuments = keysToUse
      .map((key: string): DocumentWithMetadata | null => {
        const parsed = parseDocumentKey(key);
        if (!parsed?.folio || !parsed?.businessIdentifier) {
          return null;
        }
        const { folio, businessIdentifier, stakeholderIdentifier } = parsed;
        return (
          allDocuments.find(
            (doc: DocumentWithMetadata) =>
              doc.folio === folio &&
              doc.businessIdentifier === businessIdentifier &&
              (stakeholderIdentifier == null ||
                doc.stakeholderIdentifier === stakeholderIdentifier),
          ) || null
        );
      })
      .filter((doc): doc is DocumentWithMetadata => doc !== null);

    const normalizedFoundDocuments = foundDocuments.map(
      (doc: DocumentWithMetadata) => ({
        ...doc,
        __source: targetSource,
      }),
    );

    const getDocKey = (doc: DocumentWithMetadata) =>
      doc?.id ??
      buildDocumentKey(keyType, {
        folio: doc.folio,
        businessIdentifier: doc.businessIdentifier,
        stakeholderIdentifier:
          keyType === 'check' ? doc.stakeholderIdentifier : undefined,
      });
    const combinedDocumentsMap = new Map<string, DocumentWithMetadata>(
      (selectedDocuments || []).map((doc: DocumentWithMetadata) => [
        getDocKey(doc),
        doc,
      ]),
    );
    normalizedFoundDocuments.forEach((doc: DocumentWithMetadata) => {
      const key = getDocKey(doc);
      if (key) {
        combinedDocumentsMap.set(key, doc);
      }
    });
    const combinedDocuments = Array.from(combinedDocumentsMap.values());
    setSelectedDocuments(combinedDocuments);
    setCachedDocuments(combinedDocuments);
    const combinedIds = Array.from(
      new Set(
        combinedDocuments
          .map((doc: DocumentWithMetadata) => doc?.id)
          .filter((id): id is string => Boolean(id)),
      ),
    );
    setSelected(combinedIds);

    const computeDocSource = (doc: DocumentWithMetadata): PrioritizedSource => {
      if (doc?.__source === 'manual' || doc?.__source === 'invoice') {
        return doc.__source as PrioritizedSource;
      }
      if (doc?.id && documentSources[doc.id]) {
        return documentSources[doc.id];
      }
      return enrolledType === 'manual' ? 'manual' : 'invoice';
    };

    const manualKeysFromSelection = normalizePriorityKeys(
      combinedDocuments
        .filter(
          (doc: DocumentWithMetadata) => computeDocSource(doc) === 'manual',
        )
        .map((doc: DocumentWithMetadata) =>
          buildDocumentKey(keyType, {
            folio: doc.folio,
            businessIdentifier: doc.businessIdentifier,
            stakeholderIdentifier:
              keyType === 'check' ? doc.stakeholderIdentifier : undefined,
          }),
        ),
    );
    const invoiceKeysFromSelection = normalizePriorityKeys(
      combinedDocuments
        .filter(
          (doc: DocumentWithMetadata) => computeDocSource(doc) === 'invoice',
        )
        .map((doc: DocumentWithMetadata) =>
          buildDocumentKey('invoice', {
            folio: doc.folio,
            businessIdentifier: doc.businessIdentifier,
          }),
        ),
    );

    setPersistedManualKeys(manualKeysFromSelection);
    setPersistedInvoiceKeys(invoiceKeysFromSelection);
    setDocumentSources((prev) => {
      const next = { ...prev };
      normalizedFoundDocuments.forEach((doc: DocumentWithMetadata) => {
        if (doc?.id) {
          next[doc.id] = targetSource;
        }
      });
      return next;
    });

    setPersistedManualKeys(normalizePriorityKeys(manualKeysToUse));
    setPersistedInvoiceKeys(normalizePriorityKeys(invoiceKeysToUse));
  };

  useEffect(() => {
    if (backwards && !searched) {
      void handleBackwardsPrefill();
    } else if (searched && !backwards) {
      void setAvailableSubProducts();
    }
  }, [searched]);

  return (
    <Dialog open={open} fullWidth maxWidth={searched ? 'xl' : 'sm'}>
      <DialogTitle>
        <HeaderWithActions title="Nueva solicitud" type="dialogHeader" />
      </DialogTitle>
      {loading ? (
        <CenteredCircularProgress type="dialog" />
      ) : (
        <DialogContent>
          {searched ? (
            <div>
              {business && (
                <ClientInfo
                  identifier={business.identifier}
                  businessName={business.businessName}
                  isEnrolled={isEnrolled}
                  id={business.id}
                  onChangeClient={() => {
                    setSearched(false);
                    setSearchedBySubProduct(false);
                    setBackwards(false);
                    setSelectedDocuments([]);
                    setSelected([]);
                    setCachedDocuments([]);
                    setPrioritizedDocuments({});
                    setDocumentSources({});
                    setIsEnrolled(undefined);
                    setEnrolledType(null);
                    setSubProductSelected('');
                    setSubProduct(undefined);
                  }}
                />
              )}
              <SubProductSelector
                subProducts={subProducts}
                selectedSubProduct={subProductSelected}
                onSelectSubProduct={handleSelectProduct}
                onViewDocuments={triggerSearch}
                showCreditLineWarning={createWithoutCreditline}
              />
            </div>
          ) : (
            <div>
              {!isSimulation ? (
                <CompanyBusinessSearcher
                  setBusiness={(business) => {
                    handleBusinessSelection(business as Business | null);
                  }}
                  setSearched={setSearched}
                  setIsEnrolled={setIsEnrolled}
                />
              ) : (
                <div>
                  <Box sx={{ marginBottom: '16px' }}>
                    <ToggleButtonGroup
                      value={activeTab}
                      color="primary"
                      exclusive
                      fullWidth
                      onChange={(_, newValue) => {
                        if (newValue !== null) {
                          setActiveTab(newValue);
                        }
                      }}
                      size="small"
                      sx={{
                        '& .MuiToggleButton-root': {
                          textTransform: 'none',
                          fontWeight: 500,
                        },
                      }}
                    >
                      <ToggleButton value="company">Mis clientes</ToggleButton>
                      <ToggleButton value="rut">Por RUT</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                  {activeTab === 'company' ? (
                    <CompanyBusinessSearcher
                      setBusiness={handleBusinessSelection}
                      setSearched={setSearched}
                      onSimulationSelect={handleSimulationSelect}
                      setIsEnrolled={setIsEnrolled}
                    />
                  ) : (
                    <RutBusinessSearcher
                      setBusiness={(business) => {
                        handleBusinessSelection(business as Business | null);
                      }}
                      setSearched={setSearched}
                      onSimulationSelect={handleSimulationSelect}
                      setIsEnrolled={setIsEnrolled}
                    />
                  )}
                </div>
              )}
            </div>
          )}
          {searchedBySubProduct && (
            <div style={{ padding: '20px 0px' }}>
              {isEnrolled === undefined ? (
                <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                  <CenteredCircularProgress relative />
                </Box>
              ) : (
                <>
                  <Divider />
                  <Box sx={{ mb: 3, mt: 2 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        mb: 2,
                      }}
                    >
                      <Typography variant="body1" fontWeight={600}>
                        Documentos disponibles
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <ToggleButtonGroup
                        value={enrolledType || 'manual'}
                        color="primary"
                        exclusive
                        onChange={(_, newValue) => {
                          if (newValue !== null) {
                            setEnrolledType(newValue);
                            setLoading(true);
                            void handleSearchSuggestedDocuments({
                              page: 1,
                              pageSize: PAGE_SIZE,
                              forceEnrolledType: newValue,
                              manualOverrideKeys:
                                newValue === 'manual'
                                  ? persistedManualKeys
                                  : [],
                              invoiceOverrideKeys:
                                newValue === 'sii' ? persistedInvoiceKeys : [],
                              subProductOverride: subProductSelected,
                            }).then(() => setLoading(false));
                          }
                        }}
                        size="small"
                        sx={{
                          '& .MuiToggleButton-root': {
                            textTransform: 'none',
                            fontWeight: 500,
                            minWidth: '140px',
                          },
                        }}
                      >
                        <ToggleButton
                          value="sii"
                          disabled={isEnrolled !== true}
                        >
                          Desde SII
                        </ToggleButton>
                        <ToggleButton value="manual">
                          Creados manualmente
                        </ToggleButton>
                      </ToggleButtonGroup>

                      <Box sx={{ display: 'flex', gap: '10px' }}>
                        <GlobalSearcher
                          filters={filters}
                          triggerSearch={triggerSearch}
                          dispatch={dispatch}
                          disableSearch={loading}
                        />
                        <CustomButton
                          variant="contained"
                          color="primary"
                          onClick={() => handleOpenDialogAdder()}
                        >
                          Agregar documentos
                        </CustomButton>
                      </Box>
                    </Box>
                  </Box>
                  <div>
                    <TableWithPagination
                      key={`table-${isEnrolled}-${enrolledType || 'all'}-${subProductSelected}`}
                      dataHeaders={[
                        { key: 'folio', label: 'Folio' },
                        ...(subProductSelected === 'electronic-invoice'
                          ? [{ key: 'code', label: 'Código' }]
                          : []),
                        ...(isEnrolled && enrolledType !== 'manual'
                          ? [
                              {
                                key: 'creditNoteAmount',
                                label: '',
                                type: 'custom',
                                customComponent: (value: number | string) => {
                                  return (
                                    <CreditNoteAlert
                                      creditNotesValue={Number(value) || 0}
                                    />
                                  );
                                },
                              },
                            ]
                          : []),
                        { key: 'amount', label: 'Monto', type: 'money' },
                        {
                          key: 'amountAvailable',
                          label: 'Monto disponible',
                          type: 'money',
                        },
                        {
                          key: 'stakeholderName',
                          label: 'Nombre deudor',
                        },
                        {
                          key: 'issuedDate',
                          label: 'Emisión',
                          type: 'date',
                          formatter: (value: Date) =>
                            formatDate(value, 'dd-mm-yyyy'),
                        },
                        {
                          key: 'receiptDate',
                          label: 'Recepción',
                          type: 'eight-days-date',
                        },
                        {
                          key: 'confirmDate',
                          label: 'Confirmación',
                          type: 'date',
                          formatter: (value: Date) =>
                            formatDate(value, 'dd-mm-yyyy'),
                        },
                        {
                          key: 'cancelDate',
                          label: 'Cancelación',
                          type: 'date',
                          formatter: (value: Date) =>
                            formatDate(value, 'dd-mm-yyyy'),
                        },
                        {
                          key: 'creditNoteAmount',
                          label: 'NC',
                          type: 'money',
                        },
                        {
                          key: 'requestId',
                          label: 'Solicitud',
                        },
                        ...(enrolledType === 'sii'
                          ? [
                              {
                                key: 'status',
                                label: 'Estado SII',
                                type: 'status-chip',
                              },
                            ]
                          : []),
                        ...(enrolledType === 'manual'
                          ? [
                              {
                                key: 'edit',
                                label: 'Editar',
                                type: 'custom',
                                customComponent: (
                                  _value: unknown,
                                  row: DocumentWithMetadata,
                                ) => {
                                  return (
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setDocumentToEdit(row);
                                        setOpenEditDocument(true);
                                      }}
                                      sx={{ padding: '4px' }}
                                    >
                                      <Edit fontSize="small" />
                                    </IconButton>
                                  );
                                },
                              },
                            ]
                          : []),
                      ]}
                      data={documents}
                      selected={selected}
                      setSelected={handleSetSelected}
                      searchData={handleSearchSuggestedDocuments}
                      setData={setDocuments}
                      pagination={pagination}
                      setPagination={setPagination}
                      loading={loading}
                      setLoading={setLoading}
                      cantSelectRow={cantSelectDocuments}
                      showFirstLastPageControls
                    />
                  </div>
                </>
              )}
            </div>
          )}
          <DialogActions
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            {!searched && (
              <SimulationBanner
                checked={isSimulation}
                onChange={setIsSimulation}
                sx={{ my: 0 }}
              />
            )}
            <CustomButton
              variant="contained"
              color="secondary"
              onClick={() => {
                clearPriorityKeys();
                setSelected([]);
                setCachedDocuments([]);
                setSelectedDocuments([]);
                setBusiness(null);
                setSubProductSelected('');
                setSubProduct(undefined);
                setIsEnrolled(undefined);
                setEnrolledType(null);
                setSearched(false);
                setSearchedBySubProduct(false);
                setBackwards(false);
                setOpen(false);
              }}
            >
              {' '}
              Salir
            </CustomButton>
            {searchedBySubProduct && (
              <CustomButton
                variant="contained"
                color="primary"
                disabled={!selected.length}
                onClick={handleSelectDocuments}
              >
                {' '}
                Seleccionar {selected.length > 0 && `(${selected.length})`}
              </CustomButton>
            )}
          </DialogActions>
        </DialogContent>
      )}
      {openDocumentImporter && business && documentImporterConfig && (
        <DocumentImporter
          open={openDocumentImporter}
          onClose={() => {
            setOpenDocumentImporter(false);
            setDocumentImporterConfig(null);
          }}
          config={documentImporterConfig}
          business={business}
          subProductSelected={subProductSelected}
          onImport={handleDocumentImporterImport}
          setSuccessOpen={setSuccessOpen}
        />
      )}
      {openEditDocument && business && documentToEdit && (
        <EditDocumentManually
          business={business}
          open={openEditDocument}
          setOpen={setOpenEditDocument}
          document={documentToEdit}
          onDocumentUpdated={(updatedDocument) => {
            setDocuments((prev) =>
              prev.map((doc) =>
                doc.id === updatedDocument.id ? updatedDocument : doc,
              ),
            );
            setCachedDocuments((prev) =>
              prev.map((doc) =>
                doc.id === updatedDocument.id ? updatedDocument : doc,
              ),
            );
            setOpenEditDocument(false);
            setDocumentToEdit(null);
          }}
        />
      )}
      <SimulatorDialog
        open={openSimulator}
        setOpen={setOpenSimulator}
        business={business || undefined}
        subProduct={
          subProducts.find((sp) => sp.id === subProductSelected) || undefined
        }
      />
      <SuccessSnackbar
        open={showSuccessBanner}
        message={successMessage}
        onClose={() => {
          setShowSuccessBanner(false);
        }}
      />
    </Dialog>
  );
};

export default DocumentsSelectorDialog;
